import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { convertSchedulingEventsFromCalender } from "../../../components/util/helpers";
import moment from "moment";
import ApprovalModal from "./ApprovalModal";
import EventModal from "./EventModal";
import {
  loadPage,
  editSchedulingEvent,
  changeClientTariff,
  deleteSchedulingEvent,
  createSchedulingEvent,
  reset,
} from "../schedulingSlice";
import { listHolidays } from "../../../api/server";

const PatientApprovalWrapper = (props) => {
  const { activeDate, approvalModalVisible, onHideApprovalModal } = props;
  const [currentDate, setCurrentDate] = useState(null);
  const [visible, setVisible] = useState(false);
  const [activeEvent, setActiveEvent] = useState(null);
  const [mode, setMode] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const patientId = useSelector(
    (state) => state.patientDetails.activePatient.id
  );
  const branchId = useSelector(
    (state) => state.patientDetails.activePatient.branch
  );

  const activePatient = useSelector(
    (state) => state.patientDetails.activePatient
  );

  const editLoading = useSelector(
    (state) => state.pendingActions["scheduling/edit"]
  );

  const { page: schedulingPage } = useSelector((state) => state.scheduling);
  const { effective_orders } = schedulingPage;

  const dispatch = useDispatch();

  useEffect(() => {
    setCurrentDate(moment(activeDate));
  }, [activeDate, approvalModalVisible]);

  useEffect(() => {
    const currentMonth = currentDate && moment(currentDate).month();
    const currentYear = currentDate && moment(currentDate).year();
    let startMonth = moment([currentYear, currentMonth, 1]).startOf("month");
    let endDate = startMonth.clone().endOf("month");
    if (patientId && currentDate) {
      dispatch(
        loadPage({
          query: {
            from: moment.utc(startMonth).format(),
            to: moment.utc(endDate).format(),
          },
        })
      );
    }
  }, [dispatch, currentDate, patientId]);

  useEffect(() => {
    return function cleanup() {
      dispatch(reset());
    };
  }, []);

  useEffect(() => {
    if (currentDate) {
      let filter = {
        $and: [
          {
            date: {
              $gte: moment(currentDate).startOf("month").format(),
            },
          },
          {
            date: {
              $lte: moment(currentDate).endOf("month").format(),
            },
          },
        ],
      };

      listHolidays({ filter }).then(({ error, data }) => {
        if (data) {
          setHolidays(data.items);
        }
      });
    }
  }, [currentDate]);

  const onNextMonth = () => {
    setCurrentDate(
      moment(currentDate).add(1, "months").startOf("month").format()
    );
  };
  const onPreviousMonth = () => {
    setCurrentDate(
      moment(currentDate).subtract(1, "months").startOf("month").format()
    );
  };

  const changeEvent = async (changed, start, end) => {
    setActiveEvent({ ...changed, start, end });
    const converted = convertSchedulingEventsFromCalender({
      ...changed,
      start,
      end,
    });
    const params = {
      patientId: patientId,
      branchId: branchId,
      schedulingEvent:
        activeEvent && activeEvent.approved
          ? { id: converted.id, client_tariff_id: converted.client_tariff_id }
          : { deleted: converted.deleted, new: { ...converted } },
    };
    await dispatch(
      activeEvent && activeEvent.approved
        ? changeClientTariff(params)
        : editSchedulingEvent(params)
    );
    setVisible(false);
    reload();
  };

  const createEvent = (newEvent) => {
    setActiveEvent(newEvent);
    setMode("new");
    setVisible(true);
  };

  const handleCancel = (e) => {
    setVisible(false);
    setActiveEvent(null);
  };

  const submitEvent = async (e) => {
    const converted = convertSchedulingEventsFromCalender(activeEvent);
    const params = {
      patientId: patientId,
      branchId: branchId,
      schedulingEvent: { deleted: converted.deleted, new: { ...converted } },
    };
    await dispatch(createSchedulingEvent(params));
    reload();
    setVisible(false);
  };

  const removeEvent = async (e) => {
    setVisible(false);
    const params = {
      patientId: patientId,
      branchId: branchId,
      schedulingEvent: { deleted: activeEvent ? activeEvent.id : null },
    };

    await dispatch(deleteSchedulingEvent(params));
    reload();
  };

  const changeActiveEvent = (newEvent) => {
    setActiveEvent(newEvent);
  };

  const onChosenEmployee = (employee) => {
    const client_salary_tariff_id =
      activeEvent?.order?.client?.salary_tariff_id;
    setActiveEvent({
      ...activeEvent,
      employee: employee,
      employee_id: employee.id,
      tariff_id: client_salary_tariff_id || employee.tariff_id,
      oncall: false,
      use_employee_tariff:
        client_salary_tariff_id || employee?.tariff ? true : false,
    });
  };

  const onRemoveEmployee = () => {
    setActiveEvent({
      ...activeEvent,
      employee: null,
      tariff_id: null,
      employee_id: null,
    });
  };

  const onTariffChanged = (val) => {
    setActiveEvent({
      ...activeEvent,
      tariff_id: val,
    });
  };
  const onUseEmployeeTariffChanged = (val) => {
    const clientTariffId = activeEvent?.order?.client?.tariff_id;
    setActiveEvent({
      ...activeEvent,
      use_employee_tariff: val,
      tariff_id: clientTariffId || activeEvent?.employee?.tariff?.id,
    });
  };

  const onTransportationChanged = (val) => {
    setActiveEvent({
      ...activeEvent,
      transportation_cost: val,
    });
  };

  const onOncallChange = (val) => {
    setActiveEvent({
      ...activeEvent,
      oncall: val,
    });
  };

  const reload = async () => {
    const startDate = moment(currentDate).startOf("month");
    var endDate = moment(currentDate).endOf("month");
    if (patientId) {
      await dispatch(
        loadPage({
          query: {
            from: moment.utc(startDate).format(),
            to: moment.utc(endDate).format(),
          },
        })
      );
    }
  };

  return (
    <div>
      <ApprovalModal
        onHide={onHideApprovalModal}
        visible={approvalModalVisible}
        currentDate={currentDate}
        onNextMonth={onNextMonth}
        onPreviousMonth={onPreviousMonth}
        holidays={holidays}
        createEvent={createEvent}
      />
      <EventModal
        activeEvent={activeEvent}
        changeActiveEvent={changeActiveEvent}
        orders={effective_orders}
        currentDate={currentDate}
        onChosenEmployee={onChosenEmployee}
        onRemoveEmployee={onRemoveEmployee}
        onTariffChanged={onTariffChanged}
        onUseEmployeeTariffChanged={onUseEmployeeTariffChanged}
        onTransportationChanged={onTransportationChanged}
        onOncallChange={onOncallChange}
        visible={visible}
        submitEvent={submitEvent}
        handleCancel={handleCancel}
        removeEvent={removeEvent}
        mode={mode}
        changeEvent={changeEvent}
        editLoading={editLoading}
        originalEmployeeId={null}
        patient={activePatient}
      />
    </div>
  );
};

export default PatientApprovalWrapper;
